import { CommentariesControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { MarkdownRenderer } from "$root/components/MarkdownRenderer/MarkdownRenderer";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useEventBus } from "$root/event-bus";
import { applySentimentBadges } from "$root/functional-areas/commentary/sentiment-badges";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import { type ContextContent, qualifier, withContexts } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { CommentaryContext } from "$root/widgets-architecture/contexts/commentary";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { CircularProgressBar, Column, LocalOverlay, ScrollWrapper } from "@mdotm/shadcn/legacy/components";
import { noop } from "@mdotm/shadcn/legacy/utils";

function CommentaryMDBlock(
	props: ContextContent<typeof CommentaryContext> & ContextContent<typeof UniverseFiltersContext>,
) {
	useWidgetOptions(
		() => ({
			title:
				props.fixedCommentary || (!props.firstFilter && !props.secondFilter && !props.thirdFilter)
					? "Commentary - Generative AI"
					: "Commentary",
		}),
		[props.fixedCommentary, props.firstFilter, props.secondFilter, props.thirdFilter],
	);

	return <CommentaryMDBlockContent {...props} />;
}

export function CommentaryMDBlockContent(
	props: ContextContent<typeof CommentaryContext> & ContextContent<typeof UniverseFiltersContext>,
): React.JSX.Element {
	const commentaryApi = useApiGen(CommentariesControllerApiFactory);
	const { assetClass, geography, sector } = useOutlookComposition(
		props.firstFilter,
		props.secondFilter,
		props.thirdFilter,
	);

	const query = useQueryNoRefetch(
		["commentary", props.firstFilter, props.secondFilter, props.thirdFilter, props.fixedCommentary],
		{
			queryFn: async () => {
				if (!props.firstFilter && !props.secondFilter && !props.thirdFilter) {
					const { data: dashboardCommentary } = await commentaryApi.getCommentary4(); // dashboard commentaries
					return dashboardCommentary.text && applySentimentBadges(dashboardCommentary.text);
				}

				const REGEX = { $regex: "^DASHBOARD$" };
				if (!props.fixedCommentary) {
					if (props.firstFilter && props.firstFilter?.length > 0) {
						REGEX.$regex = `^${props.firstFilter}$`;
						if (
							props.secondFilter &&
							props.secondFilter?.length > 0 &&
							props.firstFilter === "EQ" &&
							props.secondFilter !== "MARKETS"
						) {
							REGEX.$regex = `^${props.firstFilter}_${props.secondFilter}${
								props.thirdFilter && props.thirdFilter !== "ALL_SECTORS" ? `_${props.thirdFilter}` : ""
							}$`;
						}
					}
				}

				const commData = props.fixedCommentary
					? await commentaryApi.getCommentary3(assetClass, geography, sector).then((response) => ({
							...response,
							data: {
								...response.data,
								text: response.data.text && applySentimentBadges(response.data.text),
							},
						})) // outlook focus commentaries
					: await commentaryApi.getCommentary(JSON.stringify(REGEX)).then((response) => ({
							...response,
							data: {
								...response.data,
								text: response.data.text && applySentimentBadges(response.data.text),
							},
						})); // outlook commentaries
				return commData.data.text;
			},
			keepPreviousData: true,
		},
	);

	useEventBus("market-update", () => {
		query.refetch().catch(noop);
	});

	return (
		<ReactQueryWrapperBase
			query={query}
			errorFallback={<p>Generative AI commentary is not available at the moment. It will be generated soon</p>}
		>
			{(data, rq) => (
				<Column minSize="0" className="relative w-full z-0" data-qualifier={qualifier.widgets.marketCommentary.name}>
					<LocalOverlay
						show={rq.isFetching}
						className="z-10"
						style={{
							background: "white",
						}}
					>
						<CircularProgressBar value="indeterminate" />
					</LocalOverlay>

					<ScrollWrapper>
						<MarkdownRenderer>{data}</MarkdownRenderer>
					</ScrollWrapper>
				</Column>
			)}
		</ReactQueryWrapperBase>
	);
}

export default withContexts([CommentaryContext, UniverseFiltersContext])(CommentaryMDBlock);
